import React from 'react';
import { BookOpen, Zap, Lightbulb, Clock, PieChart, Home, AlertTriangle, Percent } from 'lucide-react';

/**
 * Компонент с информацией об электроэнергии в России
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <BookOpen className="w-6 h-6 text-blue-500" />
        Все об электроэнергии: тарифы, потребление, экономия
      </h2>
      
      <div className="prose prose-blue dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-yellow-500" />
          Тарифы на электроэнергию в России
        </h3>
        <p>
          <strong>Тарифы на электроэнергию</strong> в России устанавливаются региональными энергетическими комиссиями и значительно отличаются от региона к региону. На формирование тарифов влияют различные факторы: наличие генерирующих мощностей в регионе, состояние электросетей, климатические условия, а также социально-экономические показатели.
        </p>

        <div className="not-prose">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
            <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-100 dark:border-amber-800">
              <h4 className="text-base font-medium text-amber-800 dark:text-amber-300 mb-2 flex items-center">
                <Zap className="w-4 h-4 mr-2" />
                Одноставочный тариф
              </h4>
              <p className="text-sm text-amber-700 dark:text-amber-400">
                Единая стоимость электроэнергии независимо от времени суток. Самый распространенный вид тарифа, используется по умолчанию.
              </p>
            </div>
            <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-100 dark:border-indigo-800">
              <h4 className="text-base font-medium text-indigo-800 dark:text-indigo-300 mb-2 flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                Двухзонный тариф (день/ночь)
              </h4>
              <p className="text-sm text-indigo-700 dark:text-indigo-400">
                Разные ставки для дневного (7:00-23:00) и ночного (23:00-7:00) времени. Ночной тариф обычно в 2-3 раза ниже дневного.
              </p>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-100 dark:border-purple-800">
              <h4 className="text-base font-medium text-purple-800 dark:text-purple-300 mb-2 flex items-center">
                <PieChart className="w-4 h-4 mr-2" />
                Трехзонный тариф
              </h4>
              <p className="text-sm text-purple-700 dark:text-purple-400">
                Три ставки: пиковая (утро/вечер), полупиковая (день) и ночная зоны. Позволяет максимально оптимизировать расходы.
              </p>
            </div>
          </div>
        </div>

        <h3 className="flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-green-500" />
          Как экономить электроэнергию?
        </h3>
        <p>
          Снижение расходов на электроэнергию может осуществляться двумя основными способами: снижением потребления и оптимизацией тарифов. Вот несколько эффективных способов сокращения расходов:
        </p>
        <ul>
          <li>
            <strong>Переход на энергосберегающие приборы:</strong> Современная бытовая техника класса A++ и выше потребляет значительно меньше энергии при той же функциональности.
          </li>
          <li>
            <strong>Рациональное использование приборов:</strong> Выключайте неиспользуемые приборы из розетки, не оставляйте технику в режиме ожидания, используйте таймеры и режимы энергосбережения.
          </li>
          <li>
            <strong>Оптимизация освещения:</strong> Замена ламп накаливания на LED-светильники позволяет снизить энергопотребление на освещение до 90%.
          </li>
          <li>
            <strong>Установка многотарифного счетчика:</strong> При правильном распределении нагрузки по времени суток можно сэкономить до 30% на оплате электроэнергии.
          </li>
          <li>
            <strong>Использование таймеров и умных розеток:</strong> Позволяют автоматизировать включение и выключение приборов в оптимальное время.
          </li>
        </ul>

        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-100 dark:border-green-800 my-6">
          <h4 className="text-green-700 dark:text-green-300 font-medium">Кому выгоден многотарифный учет?</h4>
          <p className="text-green-600 dark:text-green-400 text-sm mb-0">
            Многотарифный учет особенно выгоден, если вы можете перенести часть потребления на ночное время: запуск стиральной или посудомоечной машины, работа бойлера, зарядка электромобиля и других устройств ночью позволит существенно сэкономить.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Home className="w-5 h-5 text-blue-500" />
          Среднее потребление электроэнергии
        </h3>
        <p>
          Среднее потребление электроэнергии различается в зависимости от типа жилья, количества проживающих, наличия электроприборов и сезона. Вот примерные показатели ежемесячного потребления для типовых сценариев:
        </p>
        <ul>
          <li><strong>Одинокий человек в небольшой квартире:</strong> 80-150 кВт⋅ч</li>
          <li><strong>Семья из 2-3 человек в квартире:</strong> 150-300 кВт⋅ч</li>
          <li><strong>Семья из 4-5 человек в квартире:</strong> 300-500 кВт⋅ч</li>
          <li><strong>Частный дом без электрического отопления:</strong> 350-700 кВт⋅ч</li>
          <li><strong>Частный дом с электрическим отоплением:</strong> 1500-3000 кВт⋅ч в зимний период</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Percent className="w-5 h-5 text-purple-500" />
          Социальные нормы и льготы
        </h3>
        <p>
          В России действует система льгот на оплату электроэнергии для определенных категорий граждан. Кроме того, в некоторых регионах действует система социальных норм потребления электроэнергии.
        </p>
        <ul>
          <li>
            <strong>Социальная норма потребления:</strong> Система, при которой электроэнергия в пределах определенного объема оплачивается по базовому тарифу, а сверх этого объема – по повышенному. Размер нормы зависит от региона и количества проживающих.
          </li>
          <li>
            <strong>Льготы для отдельных категорий граждан:</strong> Ветераны труда и ВОВ, инвалиды, многодетные семьи и другие льготные категории могут получать скидку на оплату электроэнергии (обычно 50% в пределах социальной нормы потребления).
          </li>
          <li>
            <strong>Сельские жители:</strong> В большинстве регионов для сельских жителей действует пониженный тариф на электроэнергию (примерно на 30% ниже, чем для городских).
          </li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Как правильно снимать показания счетчика
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Снимайте показания ежемесячно в одно и то же время (например, в последний день месяца)</li>
            <li>Учитывайте только целые числа на черном фоне (до запятой) для механических счетчиков</li>
            <li>Для электронных многотарифных счетчиков запишите отдельно показания для каждой тарифной зоны (обычно переключаются кнопкой на корпусе)</li>
            <li>Передавайте показания в период с 15 по 26 число каждого месяца через личный кабинет, мобильное приложение или по телефону</li>
          </ul>
        </div>

        <h3>Частые вопросы о расчете электроэнергии</h3>
        <div className="not-prose space-y-4 mt-4">
          <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Почему счетчик "мотает" быстрее, когда я включаю некоторые приборы?</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Скорость вращения диска в механическом счетчике или частота мигания светодиода в электронном напрямую зависит от мощности подключенных приборов. Чем больше мощность, тем быстрее счетчик учитывает потребление энергии. Приборы с нагревательными элементами (чайник, утюг, обогреватель) потребляют особенно много электричества.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Выгодно ли переходить на многотарифный учет?</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Это зависит от вашего графика потребления. Если вы можете перенести не менее 30% энергопотребления на ночное время, то двухтарифный учет будет выгоден. Для трехтарифного учета важно, чтобы пиковые зоны были минимально загружены. Многотарифный учет наиболее выгоден для семей, где днем никого нет дома, а основное потребление приходится на утро, вечер и выходные.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Какие приборы потребляют больше всего электроэнергии?</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Наибольшее потребление у приборов с нагревательными элементами: электрические плиты и духовки (1-3 кВт), электрические чайники (1.5-2.2 кВт), обогреватели (1-2.5 кВт), стиральные машины при нагреве воды (2-3 кВт), кондиционеры (0.7-3 кВт), электрические водонагреватели (1.5-3 кВт). Холодильники, хотя и имеют меньшую мощность (100-200 Вт), работают постоянно, что делает их одними из основных потребителей энергии в доме.
            </p>
          </div>
        </div>

        <p className="font-semibold mt-6">
          Наш калькулятор поможет вам точно рассчитать стоимость электроэнергии с учетом всех тарифов и льгот, а также проанализировать структуру потребления для поиска способов экономии!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;